import { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';

import { ManualTrade } from './TradeBlotter';

const ctx = createContext<{
    trades: ManualTrade[];
    clearTrades: () => void;
    addTrade: (trade: ManualTrade, idx?: number) => void;
    addTrades: (trades: ManualTrade[]) => void;
    updateTrade: (updatedTrade: ManualTrade, idxToReplace: number) => void;
    removeTrade: (idxToRemove: number) => void;
}>({
    trades: [],
    addTrade: () => {},
    addTrades: () => {},
    updateTrade: () => {},
    removeTrade: () => {},
    clearTrades: () => {},
});

export const TradeBlotterContext = ({ children }: PropsWithChildren) => {
    const [trades, setTrades] = useState<ManualTrade[]>([]);

    const addTrade = useCallback((trade: ManualTrade, idx?: number) => {
        setTrades((current) => {
            if (idx) {
                const result = [...current];
                result.splice(idx, 0, trade);
                return result;
            }

            return [...current, trade];
        });
    }, []);

    const addTrades = useCallback((manualTrades: ManualTrade[]) => {
        setTrades((current) => [...current, ...manualTrades]);
    }, []);

    const updateTrade = (diff: ManualTrade, idxToReplace: number) => {
        setTrades((current) => current.map((trade, idx) => (idx === idxToReplace ? { ...trade, ...diff } : trade)));
    };

    const removeTrade = useCallback((idxToRemove: number) => {
        setTrades((current) => current.filter((_, idx) => idx !== idxToRemove));
    }, []);

    const clearTrades = useCallback(() => {
        setTrades([]);
    }, []);

    return (
        <ctx.Provider value={{ trades, addTrade, addTrades, updateTrade, removeTrade, clearTrades }}>
            {children}
        </ctx.Provider>
    );
};

export const useTradeBlotterContext = () => {
    return useContext(ctx);
};
